import { AnalyticsApi } from '@liks/lac-svc-api/esm/apis/AnalyticsApi';
import type {
  ChatbotRankingDTO,
  ChatbotTenantAnalyticsDTO,
  CrawlJobPeriodCountDTO,
  CrawlJobTenantAnalyticsDTO,
  KnowledgeBaseDocumentTenantAnalyticsDTO,
  PlatformChatRankingDTO,
  PlatformChatTenantAnalyticsDTO,
  TenantOverviewDTO,
} from '@liks/lac-svc-api/esm/models/index';
import type {
  CostChartDayRow,
  CostChartMonthRow,
} from '../features/costs/costChartTypes';
import { lacApiConfiguration } from './lacApi';
import * as lacAnalyticsCostOps from './lacAnalyticsCostOps';
import * as lacAnalyticsCrawlOps from './lacAnalyticsCrawlOps';
import * as lacAnalyticsRankingOps from './lacAnalyticsRankingOps';
import type {
  LacAnalyticsListOptions,
  LacCrawlJobAnalyticsOptions,
  LacCrawlJobTimeSeriesOptions,
  LacKnowledgeBaseDocumentAnalyticsOptions,
  LacTenantOverviewDateRange,
  LacTopChatbotsOptions,
  LacTopPlatformChatsOptions,
} from './lacAnalyticsServiceTypes';

export type {
  LacAnalyticsListOptions,
  LacCrawlJobAnalyticsOptions,
  LacCrawlJobTimeSeriesOptions,
  LacKnowledgeBaseDocumentAnalyticsOptions,
  LacTenantOverviewDateRange,
  LacTopChatbotsOptions,
  LacTopPlatformChatsOptions,
} from './lacAnalyticsServiceTypes';

class LacAnalyticsService {
  private readonly analyticsApi = new AnalyticsApi(lacApiConfiguration);

  listTenantOverviews(
    range?: LacTenantOverviewDateRange,
  ): Promise<TenantOverviewDTO[]> {
    return lacAnalyticsRankingOps.listTenantOverviews(this.analyticsApi, range);
  }

  getTenantOverview(
    tenantName: string,
    range?: LacTenantOverviewDateRange,
  ): Promise<TenantOverviewDTO | null> {
    return lacAnalyticsRankingOps.getTenantOverview(
      this.analyticsApi,
      tenantName,
      range,
    );
  }

  listChatbotAnalytics(
    options: LacAnalyticsListOptions,
  ): Promise<ChatbotTenantAnalyticsDTO[]> {
    return lacAnalyticsRankingOps.listChatbotAnalytics(
      this.analyticsApi,
      options,
    );
  }

  listPlatformChatAnalytics(
    options: LacAnalyticsListOptions,
  ): Promise<PlatformChatTenantAnalyticsDTO[]> {
    return lacAnalyticsRankingOps.listPlatformChatAnalytics(
      this.analyticsApi,
      options,
    );
  }

  listTopChatbots(
    options: LacTopChatbotsOptions,
  ): Promise<ChatbotRankingDTO[]> {
    return lacAnalyticsRankingOps.listTopChatbots(this.analyticsApi, options);
  }

  listTopPlatformChats(
    options: LacTopPlatformChatsOptions,
  ): Promise<PlatformChatRankingDTO[]> {
    return lacAnalyticsRankingOps.listTopPlatformChats(
      this.analyticsApi,
      options,
    );
  }

  /**
   * @param fromDay `YYYY-MM-DD`
   * @param toDay `YYYY-MM-DD`
   */
  getDailyCosts(
    tenantName: string,
    fromDay: string,
    toDay: string,
  ): Promise<CostChartDayRow[]> {
    return lacAnalyticsCostOps.getDailyCosts(
      this.analyticsApi,
      tenantName,
      fromDay,
      toDay,
    );
  }

  /**
   * @param fromMonth `YYYY-MM`
   * @param toMonth `YYYY-MM`
   */
  getCrawlJobTimeSeries(
    options: LacCrawlJobTimeSeriesOptions,
  ): Promise<CrawlJobPeriodCountDTO[]> {
    return lacAnalyticsCrawlOps.getCrawlJobTimeSeries(
      this.analyticsApi,
      options,
    );
  }

  getCrawlJobAnalytics(
    options: LacCrawlJobAnalyticsOptions,
  ): Promise<CrawlJobTenantAnalyticsDTO | null> {
    return lacAnalyticsCrawlOps.getCrawlJobAnalytics(
      this.analyticsApi,
      options,
    );
  }

  listKnowledgeBaseDocumentAnalytics(
    options: LacKnowledgeBaseDocumentAnalyticsOptions = {},
  ): Promise<KnowledgeBaseDocumentTenantAnalyticsDTO[]> {
    return lacAnalyticsRankingOps.listKnowledgeBaseDocumentAnalytics(
      this.analyticsApi,
      options,
    );
  }

  getMonthlyCosts(
    tenantName: string,
    fromMonth: string,
    toMonth: string,
  ): Promise<CostChartMonthRow[]> {
    return lacAnalyticsCostOps.getMonthlyCosts(
      this.analyticsApi,
      tenantName,
      fromMonth,
      toMonth,
    );
  }
}

export const lacAnalyticsService = new LacAnalyticsService();
